import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    Sparkles, Send, X, Minimize2, Maximize2, Loader2,
    MessageSquare, Trash2, ChevronDown, Bot, User
} from 'lucide-react';
const API_BASE = process.env.REACT_APP_API_URL;

// Quick suggestion chips
const SUGGESTED_PROMPTS = [
    "Give me an executive summary of this survey",
    "Which questions had the lowest scores?",
    "What are the key themes in the text responses?",
    "What improvements would you suggest based on this data?",
    "How does the response rate compare to typical benchmarks?"
];

// Simple markdown-like rendering (bold, bullet points, headers)
const renderMarkdown = (text) => {
    if (!text) return null;
    const lines = text.split('\n');
    const elements = [];
    let inList = false;
    let listItems = [];

    const flushList = () => {
        if (listItems.length > 0) {
            elements.push(
                <ul key={`list-${elements.length}`} className="space-y-1 ml-4 my-2">
                    {listItems.map((item, i) => (
                        <li key={i} className="text-sm text-gray-200 list-disc">{formatInline(item)}</li>
                    ))}
                </ul>
            );
            listItems = [];
        }
        inList = false;
    };

    const formatInline = (line) => {
        // Bold
        const parts = line.split(/(\*\*.*?\*\*)/g);
        return parts.map((part, i) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={i} className="font-semibold text-white">{part.slice(2, -2)}</strong>;
            }
            return part;
        });
    };

    lines.forEach((line, idx) => {
        const trimmed = line.trim();

        if (trimmed.startsWith('### ')) {
            flushList();
            elements.push(<h4 key={idx} className="text-sm font-bold text-blue-300 mt-3 mb-1">{trimmed.slice(4)}</h4>);
        } else if (trimmed.startsWith('## ')) {
            flushList();
            elements.push(<h3 key={idx} className="text-base font-bold text-blue-200 mt-4 mb-1.5">{trimmed.slice(3)}</h3>);
        } else if (trimmed.startsWith('# ')) {
            flushList();
            elements.push(<h2 key={idx} className="text-lg font-bold text-white mt-4 mb-2">{trimmed.slice(2)}</h2>);
        } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ') || /^\d+\.\s/.test(trimmed)) {
            inList = true;
            const content = trimmed.replace(/^[-*]\s|^\d+\.\s/, '');
            listItems.push(content);
        } else if (trimmed === '') {
            flushList();
            elements.push(<div key={idx} className="h-2" />);
        } else {
            flushList();
            elements.push(<p key={idx} className="text-sm text-gray-200 leading-relaxed">{formatInline(trimmed)}</p>);
        }
    });

    flushList();
    return elements;
};

const SurveyAIChatPanel = ({ surveyId, isOpen, onToggle, onClose }) => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isStreaming, setIsStreaming] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const abortControllerRef = useRef(null);

    // Scroll to bottom on new messages
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Focus input when opened
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 300);
        }
    }, [isOpen]);

    const handleSend = useCallback(async (userMessage = null) => {
        const messageText = userMessage || input.trim();
        if (!messageText || isStreaming) return;

        const newUserMessage = { role: 'user', content: messageText };
        const updatedMessages = [...messages, newUserMessage];
        setMessages(updatedMessages);
        setInput('');
        setIsStreaming(true);

        // Add placeholder assistant message
        const assistantMessage = { role: 'assistant', content: '' };
        setMessages(prev => [...prev, assistantMessage]);

        try {
            const token = localStorage.getItem('token');
            abortControllerRef.current = new AbortController();

            const response = await fetch(`${API_BASE}/surveys/${surveyId}/ai-chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    messages: updatedMessages.map(m => ({ role: m.role, content: m.content }))
                }),
                signal: abortControllerRef.current.signal
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let fullText = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value, { stream: true });
                const lines = chunk.split('\n');

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.substring(6));
                            if (data.text) {
                                fullText += data.text;
                                setMessages(prev => {
                                    const updated = [...prev];
                                    updated[updated.length - 1] = { role: 'assistant', content: fullText };
                                    return updated;
                                });
                            }
                            if (data.message) {
                                // Error from server
                                fullText = `⚠️ ${data.message}`;
                                setMessages(prev => {
                                    const updated = [...prev];
                                    updated[updated.length - 1] = { role: 'assistant', content: fullText };
                                    return updated;
                                });
                            }
                        } catch (e) {
                            // Skip non-JSON
                        }
                    }
                }
            }
        } catch (error) {
            if (error.name === 'AbortError') return;
            console.error('AI Chat Error:', error);
            setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                    role: 'assistant',
                    content: '⚠️ Failed to get AI response. Please try again.'
                };
                return updated;
            });
        } finally {
            setIsStreaming(false);
            abortControllerRef.current = null;
        }
    }, [input, messages, isStreaming, surveyId]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleClear = () => {
        if (isStreaming) {
            abortControllerRef.current?.abort();
        }
        setMessages([]);
        setIsStreaming(false);
    };

    if (!isOpen) {
        // Collapsed FAB button
        return (
            <button
                onClick={onToggle}
                className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full shadow-lg hover:shadow-xl flex items-center justify-center text-white transition-all hover:scale-105 active:scale-95"
                title="Ask AI about this survey"
            >
                <Sparkles size={22} />
            </button>
        );
    }

    const panelWidth = isExpanded ? 'w-[600px]' : 'w-[400px]';
    const panelHeight = isExpanded ? 'h-[85vh]' : 'h-[550px]';

    return (
        <div className={`fixed bottom-6 right-6 z-50 ${panelWidth} ${panelHeight} bg-[#1a1d2e] rounded-2xl shadow-2xl border border-gray-700/50 flex flex-col overflow-hidden transition-all duration-300`}>
            {/* Header */}
            <div className="px-4 py-3 bg-gradient-to-r from-blue-600/20 to-blue-700/20 border-b border-gray-700/50 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                        <Sparkles size={16} className="text-white" />
                    </div>
                    <div>
                        <h3 className="text-sm font-bold text-white">Survey AI Analyst</h3>
                        <p className="text-[10px] text-gray-400">Ask questions about your survey data</p>
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    {messages.length > 0 && (
                        <button
                            onClick={handleClear}
                            className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                            title="Clear chat"
                        >
                            <Trash2 size={14} />
                        </button>
                    )}
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                        title={isExpanded ? "Minimize" : "Expand"}
                    >
                        {isExpanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                    </button>
                    <button
                        onClick={onClose}
                        className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                        title="Close"
                    >
                        <X size={14} />
                    </button>
                </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-gray-700">
                {messages.length === 0 ? (
                    // Welcome state with suggestions
                    <div className="h-full flex flex-col items-center justify-center text-center px-4">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500/20 to-blue-600/20 border border-blue-500/30 flex items-center justify-center mb-4">
                            <Bot size={28} className="text-blue-400" />
                        </div>
                        <h4 className="text-base font-bold text-white mb-1">Survey Insights AI</h4>
                        <p className="text-xs text-gray-400 mb-6 leading-relaxed max-w-[280px]">
                            I can analyze your survey responses, identify patterns, and suggest improvements. Try one of these:
                        </p>
                        <div className="space-y-2 w-full max-w-[320px]">
                            {SUGGESTED_PROMPTS.map((prompt, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => handleSend(prompt)}
                                    className="w-full text-left px-3.5 py-2.5 bg-white/5 hover:bg-white/10 border border-gray-700/50 hover:border-blue-500/30 rounded-xl text-xs text-gray-300 hover:text-white transition-all"
                                >
                                    {prompt}
                                </button>
                            ))}
                        </div>
                    </div>
                ) : (
                    // Chat messages
                    messages.map((msg, idx) => (
                        <div key={idx} className={`flex gap-2.5 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            {msg.role === 'assistant' && (
                                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <Sparkles size={13} className="text-white" />
                                </div>
                            )}
                            <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${msg.role === 'user'
                                ? 'bg-blue-600 text-white rounded-br-md'
                                : 'bg-[#252840] text-gray-200 rounded-bl-md border border-gray-700/30'
                                }`}>
                                {msg.role === 'user' ? (
                                    <p className="text-sm leading-relaxed">{msg.content}</p>
                                ) : msg.content ? (
                                    <div className="prose-sm">{renderMarkdown(msg.content)}</div>
                                ) : (
                                    <div className="flex items-center gap-2 py-1">
                                        <Loader2 size={14} className="animate-spin text-blue-400" />
                                        <span className="text-xs text-gray-400">Analyzing survey data...</span>
                                    </div>
                                )}
                            </div>
                            {msg.role === 'user' && (
                                <div className="w-7 h-7 rounded-lg bg-gray-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <User size={13} className="text-gray-300" />
                                </div>
                            )}
                        </div>
                    ))
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-3 border-t border-gray-700/50 flex-shrink-0">
                <div className="flex items-end gap-2 bg-[#252840] rounded-xl border border-gray-700/50 focus-within:border-blue-500/50 px-3 py-2 transition-colors">
                    <textarea
                        ref={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Ask about your survey data..."
                        rows={1}
                        className="flex-1 bg-transparent text-sm text-white placeholder-gray-500 resize-none outline-none max-h-[100px] scrollbar-thin"
                        style={{ minHeight: '24px' }}
                        disabled={isStreaming}
                    />
                    <button
                        onClick={() => handleSend()}
                        disabled={!input.trim() || isStreaming}
                        className="p-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg transition-colors flex-shrink-0"
                    >
                        {isStreaming ? (
                            <Loader2 size={16} className="animate-spin" />
                        ) : (
                            <Send size={16} />
                        )}
                    </button>
                </div>
                <p className="text-[10px] text-gray-500 mt-1.5 text-center">
                    AI analyzes your survey data. Responses may not be 100% accurate.
                </p>
            </div>
        </div>
    );
};

export default SurveyAIChatPanel;
